<template>
  <div class="blacklist-management">
    <header class="page-header">
      <div>
        <h2>黑名单管理</h2>
        <p class="subtitle">管理学员黑名单，记录黑名单原因</p>
      </div>
      <button class="primary-button" @click="openAddModal">新增黑名单</button>
    </header>

    <section class="filter-panel">
      <div class="filter-item">
        <label>关键字</label>
        <input v-model="filters.keyword" placeholder="学员姓名 / 手机号 / 身份证号" />
      </div>
      <div class="filter-item">
        <label>状态</label>
        <select v-model="filters.status">
          <option value="">全部</option>
          <option value="active">生效中</option>
          <option value="removed">已解除</option>
        </select>
      </div>
      <div class="filter-actions">
        <button class="primary-button" @click="loadBlacklist">查询</button>
        <button class="ghost-button" @click="resetFilters">重置</button>
      </div>
    </section>

    <section class="table-wrapper" v-if="blacklistRecords.length">
      <table>
        <thead>
          <tr>
            <th>学员信息</th>
            <th>身份证号</th>
            <th>加入原因</th>
            <th>操作人</th>
            <th>加入时间</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in blacklistRecords" :key="item.id">
            <td>
              <div class="student-cell">
                <span class="student-name">{{ item.studentName }}</span>
                <span v-if="item.phone && item.phone !== '00000000000'" class="student-phone">
                  {{ getDisplayValue(item.studentId, 'phone', item.phone, 'STUDENT_PROFILE') }}
                  <button class="decrypt-btn" @click="toggleDecrypt(item.studentId, 'phone', 'STUDENT_PROFILE')">
                    <svg v-if="decryptedValues['STUDENT_PROFILE_' + item.studentId + '_phone']" class="eye-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    <svg v-else class="eye-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  </button>
                </span>
              </div>
            </td>
            <td>
              {{ getDisplayValue(item.id, 'idNumber', item.idNumber) || '—' }}
              <button v-if="item.idNumber" class="decrypt-btn" @click="toggleDecrypt(item.id, 'idNumber')">
                <svg v-if="decryptedValues['BLACKLIST_' + item.id + '_idNumber']" class="eye-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg v-else class="eye-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                  <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              </button>
            </td>
            <td>
              <div class="reason-cell">{{ item.reason }}</div>
            </td>
            <td>{{ item.operatorName || '—' }}</td>
            <td>{{ formatDate(item.createdAt) }}</td>
            <td>
              <span :class="['status-tag', item.removed ? 'removed' : 'active']">
                {{ item.removed ? '已解除' : '生效中' }}
              </span>
            </td>
            <td>
              <div class="action-buttons">
                <button
                  v-if="!item.removed"
                  class="danger-link small"
                  @click="openRemoveModal(item)"
                >
                  解除黑名单
                </button>
                <button
                  v-else
                  class="primary-link small"
                  @click="reAddToBlacklist(item)"
                >
                  再次添加
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="pagination">
        <button @click="changePage(pagination.page - 1)" :disabled="pagination.page === 0 || loading">上一页</button>
        <span>第 {{ pagination.page + 1 }} / {{ totalPages }} 页</span>
        <button @click="changePage(pagination.page + 1)" :disabled="pagination.page + 1 >= totalPages || loading">
          下一页
        </button>
      </div>
    </section>

    <div v-else class="empty-state">
      <p>暂无黑名单记录</p>
    </div>

    <!-- 新增黑名单模态框 -->
    <div v-if="showAddModal" class="modal-backdrop" @click="closeAddModal">
      <div class="modal" @click.stop>
        <h3>新增黑名单</h3>
        <form @submit.prevent="submitAddBlacklist">
          <div class="form-group">
            <label>
              <span>身份证号 <span class="required">*</span></span>
              <input
                v-model="addForm.idNumber"
                @blur="validateIdNumber"
                @input="clearIdNumberError"
                placeholder="请输入18位或15位身份证号"
                required
                :class="{ 'error-input': idNumberError }"
              />
              <span v-if="idNumberError" class="error-message">{{ idNumberError }}</span>
            </label>
          </div>
          <div class="form-group">
            <label>
              <span>姓名 <span class="required">*</span></span>
              <input
                v-model="addForm.name"
                placeholder="请输入姓名"
                required
              />
            </label>
          </div>
          <div class="form-group">
            <label>
              <span>加入原因 <span class="required">*</span></span>
              <textarea
                rows="4"
                v-model="addForm.reason"
                placeholder="请详细填写加入黑名单的原因"
                required
              ></textarea>
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" class="ghost-button" @click="closeAddModal">取消</button>
            <button type="submit" class="primary-button" :disabled="loading || !addForm.idNumber.trim() || !addForm.name.trim() || !addForm.reason.trim()">
              {{ loading ? '处理中...' : '确认添加' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 解除黑名单模态框 -->
    <div v-if="showRemoveModal" class="modal-backdrop" @click="closeRemoveModal">
      <div class="modal" @click.stop>
        <h3>解除黑名单</h3>
        <div class="form-summary">
          <p><strong>学员：</strong>{{ removeForm.studentName }}（{{ removeForm.phone }}）</p>
          <p><strong>加入原因：</strong>{{ removeForm.reason }}</p>
        </div>
        <form @submit.prevent="submitRemoveBlacklist">
          <div class="form-group">
            <label>
              <span>解除备注</span>
              <textarea
                rows="3"
                v-model="removeForm.remark"
                placeholder="可选，填写解除黑名单的备注说明"
              ></textarea>
            </label>
          </div>
          <div class="modal-actions">
            <button type="button" class="ghost-button" @click="closeRemoveModal">取消</button>
            <button type="submit" class="primary-button" :disabled="loading">
              {{ loading ? '处理中...' : '确认解除' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { blacklistService } from '@/services/blacklistService'
import { sensitiveDataService } from '../services/sensitiveDataService'

export default {
  name: 'BlacklistManagement',
  data() {
    return {
      blacklistRecords: [],
      loading: false,
      filters: {
        keyword: '',
        status: ''
      },
      pagination: {
        page: 0,
        size: 10,
        total: 0
      },
      showAddModal: false,
      showRemoveModal: false,
      addForm: {
        idNumber: '',
        name: '',
        reason: ''
      },
      idNumberError: '',
      removeForm: {
        recordId: null,
        studentName: '',
        phone: '',
        reason: '',
        remark: ''
      },
      decryptedValues: {}
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.pagination.total / this.pagination.size) || 1
    }
  },
  created() {
    this.loadBlacklist()
  },
  methods: {
    async toggleDecrypt(entityId, field, entityType = 'BLACKLIST') {
      const key = `${entityType}_${entityId}_${field}`
      if (this.decryptedValues[key]) {
        delete this.decryptedValues[key]
        return
      }
      try {
        const value = await sensitiveDataService.decryptField(entityType, entityId, field)
        if (value !== undefined && value !== null) {
          this.decryptedValues[key] = value
        }
      } catch (error) {
        console.error('解密失败:', error)
        this.$message ? this.$message.error('解密失败：' + (error.message || '未知错误')) : alert('解密失败：' + (error.message || '未知错误'))
      }
    },
    getDisplayValue(entityId, field, maskedValue, entityType = 'BLACKLIST') {
      const key = `${entityType}_${entityId}_${field}`
      return this.decryptedValues[key] || maskedValue
    },
    formatDate(value) {
      if (!value) return '—'
      return value.replace('T', ' ').replace('Z', '').substring(0, 19)
    },
    async loadBlacklist() {
      try {
        this.loading = true
        const params = {
          page: this.pagination.page,
          size: this.pagination.size
        }
        const response = await blacklistService.fetchBlacklist(params)
        if (response.success) {
          let records = response.data.content || []
          
          // 前端过滤
          if (this.filters.keyword) {
            const keyword = this.filters.keyword.toLowerCase()
            records = records.filter(item => 
              item.studentName?.toLowerCase().includes(keyword) ||
              item.phone?.includes(keyword) ||
              item.idNumber?.includes(keyword)
            )
          }
          
          if (this.filters.status) {
            if (this.filters.status === 'active') {
              records = records.filter(item => !item.removed)
            } else if (this.filters.status === 'removed') {
              records = records.filter(item => item.removed)
            }
          }
          
          this.blacklistRecords = records
          this.pagination.total = response.data.totalElements || 0
        }
      } catch (error) {
        console.error('加载黑名单失败', error)
        alert(error.response?.data?.message || '加载黑名单失败')
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = {
        keyword: '',
        status: ''
      }
      this.pagination.page = 0
      this.loadBlacklist()
    },
    changePage(page) {
      if (page < 0) return
      if (page >= this.totalPages) return
      this.pagination.page = page
      this.loadBlacklist()
    },
    validateIdNumber() {
      const idNumber = this.addForm.idNumber?.trim() || ''
      this.idNumberError = ''
      
      if (!idNumber) {
        this.idNumberError = '请输入身份证号'
        return false
      }
      
      // 移除空格和特殊字符
      const cleaned = idNumber.replace(/[\s-]/g, '')
      
      // 18位身份证号验证：前17位数字，最后一位数字或X
      const id18Regex = /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/
      // 15位身份证号验证：全部数字
      const id15Regex = /^[1-9]\d{5}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}$/
      
      if (cleaned.length === 18) {
        if (!id18Regex.test(cleaned)) {
          this.idNumberError = '身份证号格式不正确，请检查18位身份证号'
          return false
        }
      } else if (cleaned.length === 15) {
        if (!id15Regex.test(cleaned)) {
          this.idNumberError = '身份证号格式不正确，请检查15位身份证号'
          return false
        }
      } else {
        this.idNumberError = '身份证号必须是15位或18位'
        return false
      }
      
      // 更新为清理后的格式（统一转换为大写X）
      this.addForm.idNumber = cleaned.toUpperCase()
      return true
    },
    clearIdNumberError() {
      this.idNumberError = ''
    },
    openAddModal() {
      this.addForm = {
        idNumber: '',
        name: '',
        reason: ''
      }
      this.idNumberError = ''
      this.showAddModal = true
    },
    closeAddModal() {
      this.showAddModal = false
      this.addForm = {
        idNumber: '',
        name: '',
        reason: ''
      }
      this.idNumberError = ''
    },
    async submitAddBlacklist() {
      // 先验证身份证号
      if (!this.validateIdNumber()) {
        return
      }
      if (!this.addForm.name || !this.addForm.name.trim()) {
        alert('请输入姓名')
        return
      }
      if (!this.addForm.reason || !this.addForm.reason.trim()) {
        alert('请填写加入黑名单的原因')
        return
      }
      try {
        this.loading = true
        const payload = {
          idNumber: this.addForm.idNumber.trim(),
          name: this.addForm.name.trim(),
          reason: this.addForm.reason.trim()
        }
        const response = await blacklistService.addToBlacklist(payload)
        if (response.success) {
          alert('添加黑名单成功')
          this.closeAddModal()
          this.loadBlacklist()
        }
      } catch (error) {
        console.error('添加黑名单失败', error)
        alert(error.response?.data?.message || error.response?.data?.error || '添加黑名单失败')
      } finally {
        this.loading = false
      }
    },
    reAddToBlacklist(item) {
      // 使用已有的身份证号和姓名，打开添加黑名单模态框
      this.addForm = {
        idNumber: item.idNumber || '',
        name: item.studentName || '',
        reason: ''
      }
      this.idNumberError = ''
      this.showAddModal = true
    },
    openRemoveModal(item) {
      this.removeForm = {
        recordId: item.id,
        studentName: item.studentName,
        phone: item.phone,
        reason: item.reason,
        remark: ''
      }
      this.showRemoveModal = true
    },
    closeRemoveModal() {
      this.showRemoveModal = false
      this.removeForm = {
        recordId: null,
        studentName: '',
        phone: '',
        reason: '',
        remark: ''
      }
    },
    async submitRemoveBlacklist() {
      if (!this.removeForm.recordId) return
      try {
        this.loading = true
        const payload = {
          recordId: this.removeForm.recordId,
          remark: this.removeForm.remark || ''
        }
        const response = await blacklistService.removeFromBlacklist(payload)
        if (response.success) {
          alert('解除黑名单成功')
          this.closeRemoveModal()
          this.loadBlacklist()
        }
      } catch (error) {
        console.error('解除黑名单失败', error)
        alert(error.response?.data?.message || '解除黑名单失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.blacklist-management {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.subtitle {
  color: #6b7280;
  margin-top: 0.25rem;
}

.filter-panel {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  background: #fff;
  padding: 1rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-item label {
  font-size: 0.85rem;
  color: #4b5563;
}

.filter-item input,
.filter-item select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.filter-actions {
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
}

.table-wrapper {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead th {
  background: #f9fafb;
  text-align: left;
  padding: 0.75rem 1rem;
  color: #6b7280;
  font-weight: 600;
  font-size: 0.9rem;
}

tbody td {
  padding: 0.8rem 1rem;
  border-top: 1px solid #f1f5f9;
  vertical-align: top;
  text-align: left;
}

.student-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.student-name {
  font-weight: 600;
  color: #111827;
}

.student-phone {
  font-size: 0.85rem;
  color: #6b7280;
}

.reason-cell {
  max-width: 300px;
  word-break: break-word;
  color: #374151;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-tag.active {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}

.status-tag.removed {
  background: rgba(107, 114, 128, 0.15);
  color: #6b7280;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.removed-label {
  color: #9ca3af;
  font-size: 0.85rem;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #f1f5f9;
  background: #fafafa;
}

.empty-state {
  text-align: center;
  color: #6b7280;
  padding: 2rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 90;
  padding: 1rem;
}

.modal {
  background: #fff;
  padding: 1.5rem;
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.modal h3 {
  margin: 0;
  color: #111827;
  font-size: 1.25rem;
  font-weight: 600;
}

.modal textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
}

.modal input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-family: inherit;
  box-sizing: border-box;
}

.modal input.error-input {
  border-color: #ef4444;
}

.error-message {
  display: block;
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.modal label {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
}

.modal label span {
  font-weight: 500;
  color: #374151;
}

.required {
  color: #ef4444;
}


.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.form-summary {
  background: #f3f4f6;
  padding: 0.75rem;
  border-radius: 8px;
  color: #4b5563;
  font-size: 0.9rem;
}

.form-group {
  margin-bottom: 1rem;
}

.primary-button,
.ghost-button,
.danger-link {
  cursor: pointer;
  border-radius: 8px;
  border: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.primary-button {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #fff;
  padding: 0.55rem 1.2rem;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.25);
}

.primary-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 24px rgba(37, 99, 235, 0.3);
}

.primary-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

.ghost-button {
  background: #f3f4f6;
  color: #374151;
  padding: 0.55rem 1.2rem;
}

.ghost-button:hover {
  background: #e5e7eb;
}

.danger-link {
  background: transparent;
  color: #ef4444;
  padding: 0.25rem 0.5rem;
}

.danger-link.small {
  font-size: 0.85rem;
}

.danger-link:hover {
  text-decoration: underline;
}

.primary-link {
  color: #2563eb;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0;
  text-decoration: none;
}

.primary-link.small {
  font-size: 0.8rem;
}

.primary-link:hover {
  text-decoration: underline;
  color: #1d4ed8;
}

.decrypt-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  margin-left: 4px;
  opacity: 0.5;
  vertical-align: middle;
}

.decrypt-btn:hover {
  opacity: 1;
  color: #409EFF;
}

.decrypt-btn .eye-icon {
  width: 16px;
  height: 16px;
}
</style>

